import { useCallback, useEffect, useState } from "react";

import { invalidateOrganizationWalletBalance } from "@/modules/dashboard/cards/hooks/useOrganizationWalletBalance";
import { WalletService } from "@/modules/dashboard/cards/services/walletsService";
import type { OrganizationWalletBalance } from "@/modules/dashboard/cards/types/wallets-api.types";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { WalletEntity } from "@/shared/types/wallet.entity";

export interface CardWalletSnapshot {
  wallet: WalletEntity;
  balance: OrganizationWalletBalance;
  /**
   * The wallet did not exist until this read provisioned it.
   *
   * Decided when the answer lands rather than during render: `Date.now()` in a component body makes it
   * non-idempotent, and the moment that matters is the read itself, not the paint.
   */
  justCreated: boolean;
}

export interface UseCardWalletForOrganizationResult {
  snapshot: CardWalletSnapshot | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/** Same window `useOrganizationWalletBalance` uses, so the two never disagree for long. */
const STALE_AFTER_MS = 30_000;

/** A wallet opened within this window of the read was almost certainly provisioned by that read. */
const JUST_CREATED_MS = 60_000;

interface Entry {
  data: CardWalletSnapshot;
  fetchedAt: number;
}

const entries = new Map<string, Entry>();
const inflight = new Map<string, Promise<CardWalletSnapshot>>();

/** Drops the cached read so the next mount or refresh goes to the network. */
export function invalidateCardWallet(organizationId: string): void {
  entries.delete(organizationId);
  invalidateOrganizationWalletBalance(organizationId);
}

function ensureFresh(organizationId: string, force: boolean): Promise<CardWalletSnapshot> {
  const cached = entries.get(organizationId);
  if (!force && cached && Date.now() - cached.fetchedAt < STALE_AFTER_MS) {
    return Promise.resolve(cached.data);
  }
  const running = inflight.get(organizationId);
  if (running) return running;

  const request = WalletService.getOrganizationCardWalletBalance(organizationId)
    .then((res) => {
      const createdAt = res.data.wallet.created_at;
      const data: CardWalletSnapshot = {
        wallet: res.data.wallet,
        balance: res.data.balance,
        justCreated: createdAt !== undefined && Date.now() - new Date(createdAt).getTime() < JUST_CREATED_MS,
      };
      entries.set(organizationId, { data, fetchedAt: Date.now() });
      return data;
    })
    .finally(() => {
      inflight.delete(organizationId);
    });

  inflight.set(organizationId, request);
  return request;
}

/**
 * The organization's card wallet and its live balance — the read that also yields the wallet id the adjust
 * endpoint needs as its path param.
 *
 * It exists instead of `useOrganizationWalletBalance` for two reasons: that hook takes no argument (it reads
 * `user.organization_id`, which for staff is Coloca's own organization, not the client's), and it returns
 * only `balance`, dropping the `wallet` object this screen needs for the id, the provider and the status.
 *
 * **Call it only for an organization this screen can serve.** `GET …/card-wallet/balance` provisions the
 * wallet when there is none, and for a Coloca Cards organization provisioning reaches the external provider —
 * so the caller passes `null` until the card program says the organization is eligible.
 */
export function useCardWalletForOrganization(
  organizationId: string | null,
): UseCardWalletForOrganizationResult {
  const [loaded, setLoaded] = useState<{ orgId: string; data: CardWalletSnapshot } | null>(null);
  const [failed, setFailed] = useState<{ orgId: string; message: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Loading is DERIVED from "a request was asked for and has not settled", so the effect never calls
  // setState on its own synchronous path.
  const [settledKey, setSettledKey] = useState<string | null>(null);

  const requestKey = organizationId ? `${organizationId}:${reloadKey}` : null;
  const isLoading = requestKey !== null && settledKey !== requestKey;

  useEffect(() => {
    if (!organizationId || !requestKey) return;
    let cancelled = false;

    ensureFresh(organizationId, reloadKey > 0)
      .then((data) => {
        if (cancelled) return;
        setLoaded({ orgId: organizationId, data });
        setFailed(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFailed({ orgId: organizationId, message: HttpApiService.getErrorMessage(err) });
      })
      .finally(() => {
        if (!cancelled) setSettledKey(requestKey);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, reloadKey, requestKey]);

  const refresh = useCallback(() => {
    if (organizationId) invalidateCardWallet(organizationId);
    setReloadKey((key) => key + 1);
  }, [organizationId]);

  // Both are keyed by organization so a previous one's wallet never flashes under a newly picked name; the
  // module cache seeds the first paint when the same organization is reopened inside the TTL.
  const cached = organizationId ? (entries.get(organizationId)?.data ?? null) : null;
  const snapshot = loaded?.orgId === organizationId ? loaded.data : cached;
  const error = failed?.orgId === organizationId ? failed.message : null;

  return { snapshot, isLoading, error, refresh };
}
